import { doorRules } from '@planner/config';
import {
  activeSizeBands,
  classifyDepthClearance,
  deriveManufacturedSize,
  findSizeAvailability,
  hasUnevenHeights,
  isOutOfSquare,
  sizeBandEnvelope,
  widthSpread,
  type ManufacturedSize,
  type SizeAvailability,
  type SizeBand,
  type SizeLimits,
} from '@planner/shared/doors';
import { hasAcknowledgement, type DoorDraft } from '../stores/doorStore.js';

export type MeasurementField =
  | 'widthTop'
  | 'widthMiddle'
  | 'widthBottom'
  | 'height'
  | 'heightRight'
  | 'doorWidth'
  | 'doorHeight'
  | 'depth';

type UnverifiedCatalogueStatus = 'loading' | 'error' | 'offline';
export type CatalogueState =
  { status: UnverifiedCatalogueStatus } | { status: 'ready'; rows: readonly SizeBand[] };

export interface MeasurementsEvaluation {
  errors: Partial<Record<MeasurementField, 'required' | 'invalid'>>;
  size: ManufacturedSize | undefined;
  limits: SizeLimits | undefined;
  availability: SizeAvailability | { status: UnverifiedCatalogueStatus | 'unverified' };
  widthDifference: number | undefined;
  heightDifference: number | undefined;
  widthWarning: boolean;
  heightWarning: boolean;
  depthStatus: ReturnType<typeof classifyDepthClearance>;
  reasons: string[];
  readyExceptCatalogue: boolean;
  canContinue: boolean;
}

export function parseMillimetres(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!/^[0-9]+$/.test(trimmed)) {
    return undefined;
  }
  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value > 0 ? value : undefined;
}

/** Recompute readiness from the current raw draft, rules and verified feed. */
export function evaluateMeasurements(
  draft: DoorDraft,
  catalogue: CatalogueState,
): MeasurementsEvaluation {
  const { measurements, doorType } = draft;
  const errors: MeasurementsEvaluation['errors'] = {};
  const read = (field: MeasurementField, raw: string): number | undefined => {
    const value = parseMillimetres(raw);
    if (value === undefined) {
      errors[field] = raw.trim() ? 'invalid' : 'required';
    }
    return value;
  };

  let size: ManufacturedSize | undefined;
  let widthDifference: number | undefined;
  let heightDifference: number | undefined;
  let widthWarning = false;
  let heightWarning = false;
  let depthStatus: MeasurementsEvaluation['depthStatus'];

  if (measurements.mode === 'diy') {
    const widthTop = read('widthTop', measurements.diy.widthTop);
    const widthMiddle = read('widthMiddle', measurements.diy.widthMiddle);
    const widthBottom = read('widthBottom', measurements.diy.widthBottom);
    const height = read('height', measurements.diy.height);
    const heightRight = read('heightRight', measurements.diy.heightRight);
    const widths =
      widthTop !== undefined && widthMiddle !== undefined && widthBottom !== undefined
        ? { widthTop, widthMiddle, widthBottom }
        : undefined;
    const heights =
      height !== undefined && heightRight !== undefined ? { height, heightRight } : undefined;
    if (widths) {
      widthDifference = widthSpread(widths);
      widthWarning = isOutOfSquare(widths) === true;
    }
    if (heights) {
      heightDifference = Math.abs(heights.height - heights.heightRight);
      heightWarning = hasUnevenHeights(heights) === true;
    }
    if (widths && heights) {
      size = deriveManufacturedSize({ mode: 'diy', ...widths, ...heights });
    }
  } else if (measurements.mode === 'trade') {
    const width = read('doorWidth', measurements.trade.width);
    const height = read('doorHeight', measurements.trade.height);
    if (width !== undefined && height !== undefined) {
      size = deriveManufacturedSize({ mode: 'trade', width, height });
    }
  }

  if (measurements.mode !== null) {
    const depth = read('depth', measurements.depth);
    depthStatus = depth === undefined ? undefined : classifyDepthClearance(depth);
  }

  const limits =
    catalogue.status === 'ready' && doorType
      ? sizeBandEnvelope(activeSizeBands(catalogue.rows, doorType))
      : undefined;
  const availability: MeasurementsEvaluation['availability'] =
    catalogue.status !== 'ready'
      ? { status: catalogue.status }
      : doorType && !limits
        ? { status: 'empty' }
        : doorType && size
          ? findSizeAvailability(catalogue.rows, doorType, size)
          : { status: 'unverified' };

  const reasons: string[] = [];
  if (!doorType) {
    reasons.push('Choose a door type.');
  }
  if (!measurements.mode) {
    reasons.push('Choose how you would like to measure.');
  }
  if (Object.keys(errors).length > 0) {
    reasons.push('Enter all required measurements as positive whole millimetres.');
  }
  if (depthStatus === 'insufficient') {
    reasons.push(`Depth clearance must be at least ${doorRules.depthClearance.min} mm.`);
  }
  if (widthWarning && !hasAcknowledgement(measurements, 'width')) {
    reasons.push('Acknowledge the uneven widths.');
  }
  if (heightWarning && !hasAcknowledgement(measurements, 'height')) {
    reasons.push('Acknowledge the uneven heights.');
  }
  if (depthStatus === 'reduced' && !hasAcknowledgement(measurements, 'depth')) {
    reasons.push('Acknowledge the reduced depth clearance.');
  }
  const readyExceptCatalogue = reasons.length === 0;
  switch (availability.status) {
    case 'loading':
      reasons.push('Checking the catalogue for available sizes.');
      break;
    case 'error':
      reasons.push('The catalogue could not be checked. Try again.');
      break;
    case 'offline':
      reasons.push('The catalogue check is paused while offline. Reconnect and try again.');
      break;
    case 'empty':
      reasons.push('No active catalogue sizes are available for this door type.');
      break;
    case 'outside-range':
    case 'gap':
    case 'invalid':
      reasons.push('This size is unavailable. Please check the range or request a custom quote.');
      break;
  }

  return {
    errors,
    size,
    limits,
    availability,
    widthDifference,
    heightDifference,
    widthWarning,
    heightWarning,
    depthStatus,
    reasons,
    readyExceptCatalogue,
    canContinue: readyExceptCatalogue && availability.status === 'available',
  };
}
